import { Button, Card, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type ScenarioRow = {
  id: string;
  name: string;
  status: string;
  objective_total?: number | null;
  hard_conflict_count?: number;
  unscheduled_count?: number;
  created_at?: string;
};

export default function SmartTimetableScenariosPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['education-smart-tt-scenarios'],
    queryFn: () => api.get<ScenarioRow[]>('/education/timetable/smart/scenarios'),
  });
  const rows = Array.isArray(query.data?.data) ? query.data!.data : [];

  return (
    <EducationPageChrome
      title={t('generationScenarios', { defaultValue: 'Generation scenarios' })}
      description={t('generationScenariosIntro', {
        defaultValue: 'Compare solver candidates. Drafts never appear as teacher/student authoritative schedules.',
      })}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/timetable/smart')}>{t('back', { defaultValue: 'Back' })}</Button>
          <Button type="primary" onClick={() => navigate('/education/timetable/smart/setup')}>
            {t('runGeneration', { defaultValue: 'Run generation' })}
          </Button>
        </Space>
      }
    >
      <Card size="small">
        <Table
          rowKey="id"
          loading={query.isLoading}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          onRow={(row) => ({
            onClick: () => navigate(`/education/timetable/smart/scenarios/${row.id}`),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: t('name'), dataIndex: 'name' },
            {
              title: t('status'),
              dataIndex: 'status',
              render: (value: string) => (
                <Tag color={value === 'validated' ? 'green' : value === 'invalid' ? 'red' : 'default'}>{value}</Tag>
              ),
            },
            { title: t('objective', { defaultValue: 'Objective' }), dataIndex: 'objective_total', width: 120 },
            { title: t('hardConflicts', { defaultValue: 'Hard conflicts' }), dataIndex: 'hard_conflict_count', width: 130 },
            { title: t('unscheduled', { defaultValue: 'Unscheduled' }), dataIndex: 'unscheduled_count', width: 120 },
            {
              title: t('createdAt', { defaultValue: 'Created' }),
              dataIndex: 'created_at',
              width: 120,
              render: (value?: string) => (value ? String(value).slice(0, 10) : '—'),
            },
          ]}
        />
      </Card>
    </EducationPageChrome>
  );
}
